/**
 * Set Card Details Panel - Slide-out panel for viewing set card information
 *
 * Shows comprehensive card information when a card is clicked in a set view.
 * Uses on-demand prefetching since set cards are from the minimal card index.
 */
import CardAvailabilitySection from '@/components/CardDetails/CardAvailabilitySection';
import CardDetailsPanelLayout from '@/components/CardDetails/CardDetailsPanelLayout';
import CardDetailsScryfallLink from '@/components/CardDetails/CardDetailsScryfallLink';
import CardShowcase from '@/components/CardDetails/CardShowcase';
import CardShowcaseSkeleton from '@/components/CardDetails/CardShowcaseSkeleton';
import EditionBrowser from '@/components/CardDetails/EditionBrowser';
import LegalitiesSection from '@/components/CardDetails/LegalitiesSection';
import PricesSection from '@/components/CardDetails/PricesSection';
import PricesSectionSkeleton from '@/components/CardDetails/PricesSectionSkeleton';
import QuickActionsBar from '@/components/CardDetails/QuickActionsBar';
import RulingsSection from '@/components/CardDetails/RulingsSection';
import RulingsSectionSkeleton from '@/components/CardDetails/RulingsSectionSkeleton';
import { collectionOwnedByScryfallIdAtom } from '@/features/collection/state/collectionAtom';
import useCardLookup from '@/hooks/useCardLookup';
import { getOracleId } from '@/lib/cardUtils';
import type { MinimalCard } from '@/services/cardDatabase';
import type { ScryfallCard } from '@/types/card';
import { useAtomValue } from 'jotai';
import type { ReactElement } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

// ============================================================================
// Types
// ============================================================================

export interface SetCardDetailsPanelProps {
  /** The scryfall ID of the card to display (null = panel closed) */
  selectedCardId: string | null;
  /** Name to show in the back button (e.g., "Back to Dominaria United") */
  setName: string;
  /** Set code (for per-set folder persistence in Add to Collection) */
  setCode?: string;
  /** Callback when panel should close */
  onClose: () => void;
}

// ============================================================================
// Component
// ============================================================================

export default function SetCardDetailsPanel({ selectedCardId, setName, setCode, onClose }: SetCardDetailsPanelProps): ReactElement | null {
  const [isLoadingPrefetch, setIsLoadingPrefetch] = useState(false);

  // Get card data
  const { getFullCard, prefetchCard, cardIndexById } = useCardLookup();
  const ownedQuantities = useAtomValue(collectionOwnedByScryfallIdAtom);

  // Get minimal card data (always available from index)
  const minimalCard: MinimalCard | null = useMemo(() => {
    if (!selectedCardId) return null;
    return cardIndexById.get(selectedCardId) ?? null;
  }, [selectedCardId, cardIndexById]);

  // Get full Scryfall card data (may need prefetching)
  const scryfallCard: ScryfallCard | null = useMemo(() => {
    if (!selectedCardId) return null;
    return getFullCard(selectedCardId);
  }, [selectedCardId, getFullCard]);

  // Prefetch full card data when card is selected
  useEffect(() => {
    if (selectedCardId && !scryfallCard) {
      setIsLoadingPrefetch(true);
      prefetchCard(selectedCardId).finally(() => {
        setIsLoadingPrefetch(false);
      });
    }
  }, [selectedCardId, scryfallCard, prefetchCard]);

  // Determine if we're in loading state
  const isLoading = isLoadingPrefetch && !scryfallCard;

  // Oracle ID for integrations
  const oracleId = getOracleId(scryfallCard) ?? minimalCard?.oracle_id ?? null;

  // log all relevant data for debugging
  useEffect(() => {
    console.log('SetCardDetailsPanel data:', {
      selectedCardId,
      setName,
      setCode,
      oracleId,
      scryfallCard,
      minimalCard,
    });
  }, [selectedCardId, setName, setCode, oracleId, scryfallCard, minimalCard]);

  // Card name (from full data, or minimal as fallback)
  const cardName = scryfallCard?.name ?? minimalCard?.name ?? 'Loading...';

  // Handle edition selection from EditionBrowser
  const handleEditionSelect = useCallback(
    async (card: MinimalCard) => {
      // Prefetch the selected edition and wait for it to load
      setIsLoadingPrefetch(true);
      await prefetchCard(card.id);
      setIsLoadingPrefetch(false);
    },
    [prefetchCard],
  );

  return (
    <CardDetailsPanelLayout
      isOpen={selectedCardId !== null}
      onClose={onClose}
      backLabel={`Back to ${setName}`}
      ariaLabelledBy="set-card-details-title"
    >
      {/* Quick Actions Bar - Star, List, Deck */}
      {oracleId && (
        <div className="border-storm shrink-0 border-b">
          <QuickActionsBar
            oracleId={oracleId}
            cardName={cardName}
            card={scryfallCard}
            scryfallId={selectedCardId ?? undefined}
            setCode={setCode}
          />
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Card Showcase (Hero) */}
        {scryfallCard ? <CardShowcase card={scryfallCard} /> : isLoading ? <CardShowcaseSkeleton /> : null}

        {/* Edition Browser */}
        {cardName && cardName !== 'Loading...' && (
          <EditionBrowser
            cardName={cardName}
            selectedId={selectedCardId ?? undefined}
            ownedQuantities={ownedQuantities}
            onSelect={handleEditionSelect}
            selectable={true}
            label="All Editions"
          />
        )}

        {/* Availability Section */}
        {oracleId && <CardAvailabilitySection oracleId={oracleId} cardName={cardName} selectedScryfallId={selectedCardId ?? undefined} />}

        {/* Prices Section */}
        {scryfallCard ? <PricesSection card={scryfallCard} /> : isLoading ? <PricesSectionSkeleton /> : null}

        {/* Rulings Section */}
        {selectedCardId && scryfallCard ? <RulingsSection cardId={selectedCardId} /> : isLoading ? <RulingsSectionSkeleton /> : null}

        {/* Legalities Section */}
        {scryfallCard && <LegalitiesSection card={scryfallCard} />}
      </div>

      {/* Footer */}
      <footer className="border-storm flex h-16 shrink-0 items-center justify-end border-t px-4">
        {scryfallCard && <CardDetailsScryfallLink set={scryfallCard.set} collectorNumber={scryfallCard.collector_number} />}
      </footer>
    </CardDetailsPanelLayout>
  );
}
